import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { getMissingFieldsData } from "../../api";
import ServiceAreaMapping from "./ServiceAreaMapping";
import PlanMapping from "./PlanMapping";
import { ProgressBar } from "../ProgressBar";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

const PlanAreaMapping = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("plan"); // Default, will be overridden by state
  const [tableData, setTableData] = useState([]);
  const [planTableData, setPlanTableData] = useState([]);
  const [serviceTableData, setServiceTableData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingData, setLoadingData] = useState(true);
  const [errorFetchingData, setErrorFetchingData] = useState(null);
  const [allTableColumns, setAllTableColumns] = useState([]); // Stores all possible columns
  const [availableTabs, setAvailableTabs] = useState([]); // Stores which tabs should be available
  const remoteUtilityId = useRemoteUtilityId();

  const { state } = useLocation();
  const hasLogged = useRef(false);
  const dataUploadId = state?.uploadId;
  const remote_utility_id = state?.remoteUtilityId;
  // const hasPlanIssue = state?.hasPlanIssue;
  // const hasPremiseIssue = state?.hasPremiseIssue;
  const [hasPlanIssue, setHasPlanIssue] = useState(state?.hasPlanIssue);
  const [hasPremiseIssue, setHasPremiseIssue] = useState(
    state?.hasPremiseIssue
  );

  // Utility function to normalize keys from API response
  const normalizeKey = (key) =>
    key
      .trim()
      .replace(/_([a-z])/g, (_, g1) => g1.toUpperCase())
      .replace(/ ([a-z])/g, (_, g1) => g1.toUpperCase());

  // Initialize tabs based on state variables
  useEffect(() => {
    if (!hasLogged.current && state) {
      console.log("Received state at planAreaMapping:", state);
      hasLogged.current = true;
    }

    // Both tabs are always available, but we determine which should be active
    setAvailableTabs(["plan", "service"]);

    // Set the active tab to the first one that has issues
    let defaultTab = "plan"; // fallback
    if (hasPlanIssue && !hasPremiseIssue) {
      defaultTab = "plan";
    } else if (!hasPlanIssue && hasPremiseIssue) {
      defaultTab = "service";
    } else if (hasPlanIssue && hasPremiseIssue) {
      defaultTab = "plan"; // If both have issues, default to plan
    }

    setActiveTab(defaultTab);
  }, [state, hasPlanIssue, hasPremiseIssue]);

  useEffect(() => {
    const fetchDataForField = async (fieldName) => {
      try {
        const response = await getMissingFieldsData(
          state?.uploadId,
          fieldName,
          remoteUtilityId
        );
        console.log("response of missing fields", response);

        if (response && response.records && response.records.length > 0) {
          const firstRecordJsonEntry = response.records[0].jsonEntry;
          let dynamicColumns = [];

          // Create columns from all keys in json_entry
          for (const rawKey in firstRecordJsonEntry) {
            if (
              Object.prototype.hasOwnProperty.call(firstRecordJsonEntry, rawKey)
            ) {
              const normalizedKey = normalizeKey(rawKey);

              // Create display label from the raw key
              const displayLabel = rawKey
                .trim()
                .replace(/_/g, " ")
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              dynamicColumns.push({
                accessor: normalizedKey,
                Header: displayLabel,
              });
            }
          }

          // Add specific mapping columns for assignment tracking
          if (fieldName === "remote_plan_id") {
            dynamicColumns.push({ accessor: "planName", Header: "Plan Name" });
          } else if (fieldName === "service_premise") {
            dynamicColumns.push({
              accessor: "serviceArea",
              Header: "Service Area",
            });
          }

          const formattedData = response.records.map((record, index) => {
            const jsonEntry = record.jsonEntry;
            const id = record.dataUploadId
              ? `${record.dataUploadId}-${index}`
              : `record-${index}`;

            // Create the base row with normalized keys
            const mappedRow = {
              id,
              ...Object.fromEntries(
                Object.entries(jsonEntry).map(([key, value]) => [
                  normalizeKey(key),
                  value,
                ])
              ),
            };

            // Add assignment status based on existing data
            if (fieldName === "remote_plan_id") {
              mappedRow.planName = jsonEntry.remotePlanId
                ? `Assigned (ID: ${jsonEntry.remotePlanId})`
                : null;
              mappedRow.planId = jsonEntry.remotePlanId || null;
            } else if (fieldName === "service_premise") {
              mappedRow.serviceArea = jsonEntry.servicePremise
                ? `Assigned (${jsonEntry.servicePremise})`
                : null;
              mappedRow.serviceAreaId = jsonEntry.servicePremise || null;
            }

            return mappedRow;
          });

          return { data: formattedData, columns: dynamicColumns };
        }

        return { data: [], columns: [] };
      } catch (error) {
        console.error(`Failed to fetch data for ${fieldName}:`, error);
        throw error;
      }
    };

    const fetchData = async () => {
      try {
        setLoadingData(true);
        setErrorFetchingData(null);

        const fetchPromises = [];
        let combinedData = [];
        let combinedColumns = [];

        // Fetch data for plan mapping if it has issues
        if (hasPlanIssue) {
          fetchPromises.push(
            fetchDataForField("remote_plan_id").then((result) => ({
              type: "plan",
              ...result,
            }))
          );
        }

        // Fetch data for service area mapping if it has issues
        if (hasPremiseIssue) {
          fetchPromises.push(
            fetchDataForField("service_premise").then((result) => ({
              type: "service",
              ...result,
            }))
          );
        }

        // Wait for all API calls to complete
        const results = await Promise.all(fetchPromises);

        // Process results
        results.forEach((result) => {
          if (result.type === "plan") {
            setPlanTableData(result.data);
          } else if (result.type === "service") {
            setServiceTableData(result.data);
          }

          // Merge columns (avoid duplicates)
          result.columns.forEach((col) => {
            if (
              !combinedColumns.find(
                (existing) => existing.accessor === col.accessor
              )
            ) {
              combinedColumns.push(col);
            }
          });

          // Combine data
          combinedData = [...combinedData, ...result.data];
        });

        setAllTableColumns(combinedColumns);
        setTableData(combinedData);
      } catch (error) {
        console.error("Failed to fetch table data:", error);
        setErrorFetchingData("Failed to load records. Please try again.");
        setTableData([]);
        setPlanTableData([]);
        setServiceTableData([]);
        setAllTableColumns([]);
      } finally {
        setLoadingData(false);
      }
    };

    if (dataUploadId && (hasPlanIssue || hasPremiseIssue)) {
      fetchData();
    } else {
      setLoadingData(false);
      if (!dataUploadId) {
        setErrorFetchingData("Missing data upload ID.");
      } else if (!hasPlanIssue && !hasPremiseIssue) {
        setErrorFetchingData("No mapping issues found.");
      }
    }
  }, [dataUploadId, remote_utility_id, hasPlanIssue, hasPremiseIssue]);

  // Get current tab's specific data
  const getCurrentTabData = () => {
    if (activeTab === "plan") {
      return hasPlanIssue ? planTableData : [];
    } else if (activeTab === "service") {
      return hasPremiseIssue ? serviceTableData : [];
    }
    return [];
  };

  const getMappedCount = (tab) => {
    const data = tab === "plan" ? planTableData : serviceTableData;
    if (data.length === 0) return 0;
    return tab === "plan"
      ? data.filter((row) => row.planName && row.planId).length
      : data.filter((row) => row.serviceArea && row.serviceAreaId).length;
  };

  const currentTabData = getCurrentTabData();
  const progress =
    currentTabData.length > 0
      ? Math.round((getMappedCount(activeTab) / currentTabData.length) * 100)
      : 0;

  // Filter columns based on the active tab
  const getCurrentTabColumns = () => {
    if (!allTableColumns.length) return [];

    if (activeTab === "plan") {
      // For plan tab, exclude serviceArea column
      return allTableColumns.filter(
        (column) => column.accessor !== "serviceArea"
      );
    } else if (activeTab === "service") {
      // For service tab, exclude planName column
      return allTableColumns.filter((column) => column.accessor !== "planName");
    }
    return allTableColumns;
  };

  const handleBackToFieldMapping = () => {
    console.log("Navigate back to field mapping");
  };

  useEffect(() => {
    console.log("Updated hasPlanIssue:", hasPlanIssue);
  }, [hasPlanIssue]);

  const handleContinueToValidation = () => {
    console.log("hasPlanIssue at parent", hasPlanIssue);
    console.log("hasPremiseIssue", hasPremiseIssue);

    if (hasPlanIssue && hasPremiseIssue) {
      // toast({
      //   title: "Validation Error",
      //   description: "Please resolve both Plan and Premise issues.",
      //   variant: "destructive",
      // });
      // return;
    }

    if (hasPlanIssue) {
      console.log("plan issues present");

      // toast({
      //   title: "Plan Issue",
      //   description: "Please assign a plan before continuing.",
      //   variant: "destructive",
      // });
      // return;
    }

    if (hasPremiseIssue) {
      console.log("premise issues present");

      // toast({
      //   title: "Premise Issue",
      //   description: "Please assign a premise before continuing.",
      //   variant: "destructive",
      // });
      // return;
    }

    // ✅ All good — navigate forward
    navigate("/onboarding/data-migration/data-validation", {
      state: {
        ...state,
        hasPlanIssue,
        hasPremiseIssue,
      },
    });
  };

  // Handle tab change - only allow if tab has issues (is enabled)
  const handleTabChange = (tab) => {
    const isTabEnabled =
      (tab === "plan" && hasPlanIssue) ||
      (tab === "service" && hasPremiseIssue);
    if (isTabEnabled) {
      setActiveTab(tab);
      // Reset selected rows when switching tabs
      setSelectedRows([]);
    }
  };

  // Update table data when tab changes
  const updateCurrentTabData = (newData) => {
    if (activeTab === "plan") {
      setPlanTableData(newData);
    } else if (activeTab === "service") {
      setServiceTableData(newData);
    }

    // Also update the combined tableData
    const otherTabData =
      activeTab === "plan" ? serviceTableData : planTableData;
    setTableData([...newData, ...otherTabData]);
  };

  // Check if a tab is disabled
  const isTabDisabled = (tab) => {
    return (
      (tab === "plan" && !hasPlanIssue) ||
      (tab === "service" && !hasPremiseIssue)
    );
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-lg text-gray-700">Loading records...</div>
      </div>
    );
  }

  if (errorFetchingData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-lg text-red-600">Error: {errorFetchingData}</div>
      </div>
    );
  }

  if (tableData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-lg text-gray-700">
          No records found for mapping.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-2">
      <ProgressBar
        currentStep={4}
        totalSteps={7}
        progress={56}
        currentStepName={"Plan And Service Area Mapping"}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            System Data Mapping
          </h1>
          <p className="text-gray-600">
            Assign system fields to your imported records
          </p>
        </div>

        {/* Tabs - Always show both tabs, disable based on issues */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {["plan", "service"].map((tab) => {
                const isDisabled = isTabDisabled(tab);
                const hasIssue =
                  (tab === "plan" && hasPlanIssue) ||
                  (tab === "service" && hasPremiseIssue);

                return (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    disabled={isDisabled}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab && !isDisabled
                        ? "border-blue-500 text-blue-600"
                        : isDisabled
                        ? "border-transparent text-gray-400 cursor-not-allowed"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {tab === "plan" ? "Plan Mapping" : "Service Area Mapping"}
                    {/* Show issue indicator for enabled tabs with issues */}
                    {/* {hasIssue && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Issues Found
                      </span>
                    )} */}
                    {/* Show disabled indicator */}
                    {/* {isDisabled && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        No Issues
                      </span>
                    )} */}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Render appropriate mapping component */}
        {activeTab === "plan" && hasPlanIssue && (
          <PlanMapping
            tableData={currentTabData}
            setTableData={updateCurrentTabData}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activeTab={activeTab}
            progress={progress}
            getMappedCount={getMappedCount}
            tableColumns={getCurrentTabColumns()}
            uploadId={dataUploadId}
            setHasPlanIssue={setHasPlanIssue}
          />
        )}

        {activeTab === "service" && hasPremiseIssue && (
          <ServiceAreaMapping
            tableData={currentTabData}
            setTableData={updateCurrentTabData}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activeTab={activeTab}
            progress={progress}
            getMappedCount={getMappedCount}
            tableColumns={getCurrentTabColumns()}
            uploadId={dataUploadId}
            setHasPremiseIssue={setHasPremiseIssue}
          />
        )}

        {/* Show message when tab is disabled */}
        {((activeTab === "plan" && !hasPlanIssue) ||
          (activeTab === "service" && !hasPremiseIssue)) && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">
              No mapping issues found for{" "}
              {activeTab === "plan" ? "Plan Mapping" : "Service Area Mapping"}
            </div>
            <div className="text-gray-400 text-sm mt-2">
              All records have been properly mapped for this category.
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="flex items-center justify-center mt-6">
          <div className="flex items-center space-x-2">
            <button className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <div className="w-64 bg-gray-200 rounded-full h-2">
              <div
                className="bg-gray-400 h-2 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <button className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleBackToFieldMapping}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={16} />
            <span>Back to Field Mapping</span>
          </button>
          <button
            onClick={handleContinueToValidation}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span>Continue to Validation</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanAreaMapping;
